import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { GroupLabel, KitButton, KitCard, KitItem, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitChip, KitChips, KitEmpty, KitField, KitInput, KitSelect } from '../common/MobileKitExtras';
import { useAppBarAction, useAppBarSubtitle } from '../layout/AppBarActions';

/**
 * Próximos serviços (recorrência por veículo) no shell mobile —
 * `prototipos-mobile/ordens/proximos.html`. Só apresentação: filtros, paginação,
 * aviso por WhatsApp e navegação vêm de `pages/service_order/UpcomingServices.js`.
 */

const CarIcon = protoIcon('car');
const MsgIcon = protoIcon('msg');
const FunnelIcon = protoIcon('funnel');
const CalendarIcon = protoIcon('calendar');
const InfoIcon = protoIcon('info');

const plural = (n, singular, pluralText) => (Math.abs(n) === 1 ? singular : pluralText);

// "venceu há 3 dias" / "vence hoje" / "em 3 dias"
function describeDays(days) {
    if (days === null || days === undefined) return '';
    if (days < 0) return `venceu há ${Math.abs(days)} ${plural(days, 'dia', 'dias')}`;
    if (days === 0) return 'vence hoje';
    return `em ${days} ${plural(days, 'dia', 'dias')}`;
}

const formatKm = (value) => (value === null || value === undefined ? '-' : `${Number(value).toLocaleString('pt-BR')} km`);

function describeKm(remaining) {
    if (remaining === null || remaining === undefined) return '';
    if (remaining < 0) return `${formatKm(Math.abs(remaining))} acima`;
    if (remaining === 0) return 'atingido';
    return `faltam ${formatKm(remaining)}`;
}

// Pill do protótipo (Atrasado / Em breve) + "Em dia" para o terceiro status real.
const PILL = {
    vencido: { tone: 'danger', label: 'Atrasado' },
    a_vencer: { tone: 'warning', label: 'Em breve' },
    em_dia: { tone: 'success', label: 'Em dia' },
};

const GROUPS = [
    { key: 'late', label: 'Atrasado' },
    { key: 'd7', label: 'Próximos 7 dias' },
    { key: 'd30', label: 'Próximos 30 dias' },
    { key: 'later', label: 'Mais adiante' },
    { key: 'km', label: 'Por quilometragem' },
];

function bucketOf(item) {
    if (item.status === 'vencido' || (item.days_remaining !== null && item.days_remaining !== undefined && item.days_remaining < 0)) return 'late';
    if (item.days_remaining === null || item.days_remaining === undefined) return 'km';
    if (item.days_remaining <= 7) return 'd7';
    if (item.days_remaining <= 30) return 'd30';
    return 'later';
}

export const UPCOMING_CHIPS = [
    { key: 'all', label: 'Todos' },
    { key: 'late', label: 'Atrasados' },
    { key: '7', label: '7 dias' },
    { key: '30', label: '30 dias' },
];

function UpcomingCard({ item, canGenerate, onGenerate, onReminder, sendingKey, reminderKey }) {
    const pill = PILL[item.status] || { tone: undefined, label: item.status || '—' };
    const vehicle = [item.vehicle_model, item.license_plate].filter(Boolean).join(' · ') || '—';
    const when = item.next_date ? describeDays(item.days_remaining) : describeKm(item.km_remaining);
    const noPhone = !String(item.customer_phone || '').trim();
    const noDate = !item.next_date;
    const sending = sendingKey === reminderKey(item);
    const title = noPhone ? 'Cliente sem telefone cadastrado' : noDate ? 'Serviço sem intervalo de tempo: aviso indisponível' : 'Enviar aviso por WhatsApp';
    return (
        <KitCard sx={{ p: '4px 10px 12px', overflow: 'hidden' }}>
            <KitItem
                icon={CarIcon}
                title={vehicle}
                sub={[item.service_name, when].filter(Boolean).join(' · ')}
                end={<KitPill tone={pill.tone}>{pill.label}</KitPill>}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '6px' }}>
                <KitButton variant="soft" size="sm" disabled={noPhone || noDate || Boolean(sendingKey)} title={title} onClick={() => onReminder(item)}>
                    <MsgIcon size={18} /> {sending ? 'Enviando…' : 'WhatsApp'}
                </KitButton>
                {canGenerate && (
                    <KitButton variant="primary" size="sm" sx={{ flex: 1, minWidth: 0 }} onClick={() => onGenerate(item)}>
                        Gerar OS
                    </KitButton>
                )}
            </Box>
        </KitCard>
    );
}

export default function MobileUpcoming({
    usesVehicle,
    loadingSettings,
    items,
    loading,
    errorMessage,
    onRetry,
    totalCount,
    hasMore,
    onLoadMore,
    chip,
    onChipChange,
    filters,
    onApplyFilters,
    onClearFilters,
    customers,
    services,
    canGenerate,
    onGenerate,
    onReminder,
    sendingKey,
    reminderKey,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draft, setDraft] = useState(filters);

    useAppBarSubtitle('Recorrência por veículo');
    useAppBarAction({
        label: 'Filtrar',
        icon: FunnelIcon,
        enabled: usesVehicle,
        onClick: () => {
            setDraft(filters);
            setFiltersOpen(true);
        },
    });

    const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const grouped = GROUPS.map((group) => ({ ...group, items: items.filter((item) => bucketOf(item) === group.key) })).filter((group) => group.items.length > 0);

    if (!loadingSettings && !usesVehicle) {
        return (
            <KitBanner tone="info" icon={InfoIcon}>
                A recorrência de serviços depende do veículo do cliente. Ative a opção "Usa veículo" em Configurações para usar esta tela.
            </KitBanner>
        );
    }

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitChips>
                {UPCOMING_CHIPS.map((c) => (
                    <KitChip key={c.key} active={chip === c.key} count={chip === c.key && !loading ? totalCount : undefined} onClick={() => onChipChange(c.key)}>
                        {c.label}
                    </KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />

            {errorMessage && (
                <Box sx={{ mt: '6px' }}>
                    <KitBanner tone="danger" title={errorMessage}>
                        <Box component="button" type="button" onClick={onRetry} sx={{ border: 0, background: 'none', p: 0, mt: '6px', fontFamily: 'inherit', fontWeight: 700, fontSize: 13, color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>
                            Tentar novamente
                        </Box>
                    </KitBanner>
                </Box>
            )}

            {loading && items.length === 0 && !errorMessage && (
                <Box sx={{ mt: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={112} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && !errorMessage && items.length === 0 && (
                <KitEmpty icon={CalendarIcon} title="Nenhum serviço encontrado">
                    Serviços só aparecem aqui depois de executados em uma ordem de serviço com veículo.
                </KitEmpty>
            )}

            {grouped.map((group) => (
                <Box key={group.key}>
                    <GroupLabel>{group.label}</GroupLabel>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {group.items.map((item) => (
                            <UpcomingCard
                                key={reminderKey(item)}
                                item={item}
                                canGenerate={canGenerate}
                                onGenerate={onGenerate}
                                onReminder={onReminder}
                                sendingKey={sendingKey}
                                reminderKey={reminderKey}
                            />
                        ))}
                    </Box>
                </Box>
            ))}

            {hasMore && (
                <KitButton block sx={{ mt: '4px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtrar"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onClearFilters();
                            }}
                        >
                            Limpar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onApplyFilters(draft);
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Veículo">
                    <KitInput aria-label="Veículo" placeholder="Placa ou modelo…" value={draft.vehicle} onChange={(e) => setField('vehicle', e.target.value)} />
                </KitField>
                <KitField label="Cliente">
                    <KitSelect aria-label="Cliente" value={draft.customerId} onChange={(e) => setField('customerId', e.target.value)}>
                        <option value="">Todos</option>
                        {customers.map((c) => <option key={c.ID} value={c.ID}>{c.Name}</option>)}
                    </KitSelect>
                </KitField>
                <KitField label="Serviço">
                    <KitSelect aria-label="Serviço" value={draft.serviceId} onChange={(e) => setField('serviceId', e.target.value)}>
                        <option value="">Todos</option>
                        {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </KitSelect>
                </KitField>
            </MobileSheet>
        </Box>
    );
}
